import type { AnimationPrefetchContext, SceneLayer } from "@/layers";
import { type ScenePublicApi, _debug_scenes } from "@/layers/Compositor";
import { setTimeList } from "@/time/helper-function";
import { isAbortable, isAbortedRequest } from "@mm/api.meteomatics.com";
import Logger from "@mm/logging";
import type { DateTime, Duration } from "luxon";
import { Interval } from "luxon";

const logger = Logger.fromFilename(__filename);

function isCancelledFetchError(error: unknown): boolean {
  if (isAbortable(error) || isAbortedRequest(error)) {
    return true;
  }

  return (
    typeof error === "object" &&
    error !== null &&
    "isCancelled" in error &&
    (error as { isCancelled?: unknown }).isCancelled === true
  );
}

export function convertToLastPastIntervalTime(
  newActiveTime: DateTime,
  interval: Interval,
  temporalResolution: Duration,
): DateTime {
  const extraStepEndTime = interval.end.plus(temporalResolution);
  const timeList = setTimeList(Interval.fromDateTimes(interval.start, extraStepEndTime), temporalResolution);
  const foundIdx = timeList.findIndex((time, index, list) => {
    return time > newActiveTime;
  });
  let setIndex = 0;
  if (foundIdx > 0) {
    setIndex = foundIdx - 1;
  }
  return timeList[setIndex];
}

export function fetchAllData(datetime: DateTime, animationPrefetchContext?: AnimationPrefetchContext) {
  // Loop over all active scenes and call the fetch function
  for (const [, { inUse, obj: scene }] of _debug_scenes()) {
    if (!inUse || !scene.isActive) {
      continue;
    }
    sceneFetchAllData(scene, datetime, animationPrefetchContext);
  }
}

function sceneFetchAllData(
  scene: ScenePublicApi,
  datetime: DateTime,
  animationPrefetchContext?: AnimationPrefetchContext,
) {
  for (const layer of scene.layers) {
    if (layer === undefined || !layer.getLayerProps().show) {
      continue;
    }

    void layer.fetchData(datetime, animationPrefetchContext).asynchronous.catch((error: unknown) => {
      if (isCancelledFetchError(error)) {
        return;
      }

      logger.error("Failed to fetch scene layer data", layer.uid, error);
    });
  }
}

export function triggerUpdateData() {
  for (const [, { inUse, obj: scene }] of _debug_scenes()) {
    if (!inUse || !scene.isActive) {
      continue;
    }
    sceneTriggerReFetchAllData(scene);
  }
}

function sceneTriggerReFetchAllData(scene: ScenePublicApi) {
  for (const layer of scene.layers) {
    if (layer === undefined || !layer.getLayerProps().show) {
      continue;
    }

    if (layer.triggerRefetchData) {
      layer.triggerRefetchData();
    }
  }
}
export function isRebuffering(datetime: DateTime): boolean {
  // Array.from use => if there is not a high amount of scenes ( < 5000 ) then it's fine
  return Array.from(_debug_scenes()).some(
    ([, { inUse, obj: scene }]) => inUse && scene.isActive && sceneIsRebuffering(scene, datetime),
  );
}

function sceneIsRebuffering(scene: ScenePublicApi, datetime: DateTime): boolean {
  return scene.layers.some((layer) => layer?.getLayerProps().show && layer.isRebuffering(datetime));
}

type AllScenesLoadedOptions = {
  /**
   * If true, also require "next frame" readiness in addition to current frame readiness.
   * This is useful for smooth playback prefetching but can over-wait for export.
   */
  checkNextFrameReadiness?: boolean;
  /**
   * If true, ignore scenes that are not active.
   */
  onlyActiveScenes?: boolean;
  /**
   * If true, wait for an explicit map render tick before resolving.
   * Disabling this can improve export throughput when frames are already cached.
   */
  waitForMapRender?: boolean;
  /**
   * If true, defer readiness resolution to the next animation frame.
   * Disabling this removes one fixed frame delay per loaded frame.
   */
  resolveReadyLayersOnAnimationFrame?: boolean;
};

async function allScenesAreIdle(time: DateTime, lastFrame: boolean, options?: AllScenesLoadedOptions) {
  const checkNextFrameReadiness = options?.checkNextFrameReadiness ?? true;
  const onlyActiveScenes = options?.onlyActiveScenes ?? false;
  const waitForMapRender = options?.waitForMapRender ?? true;
  const resolveReadyLayersOnAnimationFrame = options?.resolveReadyLayersOnAnimationFrame ?? true;
  const promises: Promise<void>[] = [];
  for (const [, { inUse, obj: scene }] of _debug_scenes()) {
    if (!inUse) {
      continue;
    }
    if (onlyActiveScenes && !scene.isActive) {
      continue;
    }

    promises.push(
      new Promise((resolve) => {
        // Create waiting promises only for visible layers
        const perLayersPromises = scene.layers
          .filter((layer) => layer.getLayerProps().show)
          .map((layer) =>
            createPerLayerLoadedPromise(
              layer,
              time,
              lastFrame,
              scene.nextTime,
              checkNextFrameReadiness,
              resolveReadyLayersOnAnimationFrame,
            ),
          );
        Promise.all(perLayersPromises).then(() => {
          if (!waitForMapRender) {
            resolve();
            return;
          }
          scene.getMapboxMap().once("render", () => {
            resolve();
          });

          scene.getMapboxMap().triggerRepaint();
        });
      }),
    );
  }

  await Promise.all(promises);
}

export function allScenesLoaded(time: DateTime, lastFrame: boolean, options?: AllScenesLoadedOptions) {
  return new Promise((resolve) => {
    // Wait while all scenes are idle and only then resolve promise
    // to make sure mapbox are finished loading and drawing everything

    allScenesAreIdle(time, lastFrame, options)
      .then(() => {
        resolve(true);
      })
      .catch((e) => console.log(e));
  });
}

function createPerLayerLoadedPromise(
  layer: SceneLayer,
  time: DateTime,
  lastFrame: boolean,
  nextTime: DateTime,
  checkNextFrameReadiness: boolean,
  resolveReadyLayersOnAnimationFrame: boolean,
) {
  const checkRebufferingStatus = (time: DateTime, cb: () => void) => {
    const nextFrameIsReady = !checkNextFrameReadiness || lastFrame || !layer.isRebuffering(nextTime);
    if (!layer.isRebuffering(time) && nextFrameIsReady) {
      if (resolveReadyLayersOnAnimationFrame) {
        requestAnimationFrame(() => cb());
      } else {
        cb();
      }
      return;
    }
    requestAnimationFrame(() => checkRebufferingStatus(time, cb));
  };

  return new Promise<void>((resolve) => {
    checkRebufferingStatus(time, () => resolve());
  });
}
